import { kucukTr, metinOku, sayiOku, yuvarla2 } from '../rapor-ekrani/bicim';
import type { GrupAyari, RaporKolonu } from '../rapor-ekrani/kolon';

/**
 * S345 Stok Değer ve Stok Çıkış raporları: ds satırlarının ekran karşılıkları. Kolon adları VERİ
 * SÖZLEŞMESİDİR (`cdc-reports/sql/vega/179-erp12-stok-deger-cikis-ds.sql`); ham satır burada bir kez
 * okunur, ekran ham alana dokunmaz.
 */

type OzelKod = `ozel_kod_${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}`;
/** Stok kartının gruplanabilen alanları; ad aynı zamanda ds kolonunun adıdır. */
export type GruplamaAlani = 'urun_grubu' | 'stok_cinsi' | 'marka' | OzelKod | 'tedarikci';

export const GRUPLAMA_ALANLARI: ReadonlyArray<{ id: GruplamaAlani; etiket: string }> = [
  { id: 'urun_grubu', etiket: 'Ürün Grubu' },
  { id: 'stok_cinsi', etiket: 'Stok Cinsi' },
  { id: 'marka', etiket: 'Marka' },
  ...([1, 2, 3, 4, 5, 6, 7, 8, 9] as const).map((n) => ({ id: `ozel_kod_${n}` as const, etiket: `Özel Kod ${n}` })),
  { id: 'tedarikci', etiket: 'Tedarikçi' },
];

/** Gruplanmadıkları sürece tabloda kolon olarak da duran alanlar (demodaki `stokEtiket`). */
const KOLON_ALANLARI: readonly GruplamaAlani[] = ['urun_grubu', 'marka', 'tedarikci'];

export const gruplamaEtiketi = (alan: GruplamaAlani) => GRUPLAMA_ALANLARI.find((g) => g.id === alan)!.etiket;

/** İki listenin ortak ürün alanları. Gruplama alanı kartta boşsa NULL gelir. */
export interface HamStokAlanlari extends Partial<Record<GruplamaAlani, string | null>> {
  stok_id: number | string;
  stok_kodu: string | null;
  urun: string | null;
}

export interface StokAlanlari {
  id: string;
  kod: string;
  ad: string;
  /** Gruplama alanlarının değerleri; kartta boş olan ''. */
  kodlar: Record<GruplamaAlani, string>;
  /** Arama anahtarı (kod + ad, Türkçe küçük harf). */
  arama: string;
}

function stokAlanlari(h: HamStokAlanlari): StokAlanlari {
  const kod = metinOku(h.stok_kodu);
  const ad = metinOku(h.urun);
  const kodlar = {} as Record<GruplamaAlani, string>;
  for (const g of GRUPLAMA_ALANLARI) kodlar[g.id] = metinOku(h[g.id]);
  return { id: String(h.stok_id), kod, ad, kodlar, arama: kucukTr(`${kod} ${ad}`) };
}

/** Firmanın verisinde EN AZ BİR üründe dolu olan gruplama alanları (seçenek ve kolon yalnız bunlardan). */
export function doluAlanlar(satirlar: readonly StokAlanlari[]): GruplamaAlani[] {
  return GRUPLAMA_ALANLARI.filter((g) => satirlar.some((s) => s.kodlar[g.id] !== '')).map((g) => g.id);
}

/** Ürün grubu süzgecinin seçenekleri (Türkçe sıralı). */
export function urunGruplari(satirlar: readonly StokAlanlari[]): string[] {
  const kume = new Set<string>();
  for (const s of satirlar) if (s.kodlar.urun_grubu !== '') kume.add(s.kodlar.urun_grubu);
  return Array.from(kume).sort((a, b) => a.localeCompare(b, 'tr'));
}

/**
 * Ürün Grubu / Marka / Tedarikçi kolonları: yalnız verisi olanlar. Kolon kimliği gruplama alanının
 * kimliğidir; `RaporTablosu` gruplanan alanın kolonunu kendisi çıkarır. Telefon kartında yoklar.
 */
export function grupKolonlari<S extends StokAlanlari>(dolu: readonly GruplamaAlani[]): RaporKolonu<S>[] {
  return KOLON_ALANLARI.filter((a) => dolu.includes(a)).map((a) => ({
    id: a, baslik: gruplamaEtiketi(a), tur: 'metin', deger: (s) => s.kodlar[a], kart: 'gizli',
  }));
}

/** Boş kod "(Tanımsız)" grubuna düşer (`kolon.gruplaSatirlar`). */
export function grupAyari<S extends StokAlanlari>(alan: GruplamaAlani): GrupAyari<S> {
  return { id: alan, deger: (s) => s.kodlar[alan], birim: 'ürün' };
}

/** Bilgi satırındaki grup sayısı; boş kodlar tek grup sayılır ("(Tanımsız)"). Gruplama yoksa 0. */
export function grupSayisi(satirlar: readonly StokAlanlari[], alan: GruplamaAlani | ''): number {
  return alan === '' ? 0 : new Set(satirlar.map((s) => s.kodlar[alan])).size;
}

function urunSuz<S extends StokAlanlari>(satirlar: readonly S[], urunGrubu: string, ara: string): S[] {
  const q = kucukTr(ara.trim());
  return satirlar.filter((s) => (urunGrubu === '' || s.kodlar.urun_grubu === urunGrubu) && (q === '' || s.arama.includes(q)));
}

// ── Stok Değer ──────────────────────────────────────────────────────────────────

/** `liste` ds'i, parametre `@LOKASYON` (boş = tümü). Satır = ürün. */
export interface HamStokDegerSatiri extends HamStokAlanlari {
  eldeki_miktar: number | string | null;
  son_alis_fiyati: number | string | null;
  agirlikli_ortalama_fiyat: number | string | null;
  son_alis_tarihi: string | null;
}

export interface StokDegerSatiri extends StokAlanlari {
  elde: number;
  sonAlis: number;
  agOrt: number;
  sonAlisTarihi: string | null;
}

export function stokDegerSatiri(h: HamStokDegerSatiri): StokDegerSatiri {
  return {
    ...stokAlanlari(h),
    elde: sayiOku(h.eldeki_miktar),
    sonAlis: sayiOku(h.son_alis_fiyati),
    agOrt: sayiOku(h.agirlikli_ortalama_fiyat),
    sonAlisTarihi: h.son_alis_tarihi || null,
  };
}

export type Degerleme = 'son' | 'agort';

export const DEGERLEME_SECENEKLERI: ReadonlyArray<{ key: Degerleme; label: string }> = [
  { key: 'son', label: 'Son alış' },
  { key: 'agort', label: 'Ağırlıklı ort.' },
];

/** Bilgi satırındaki ad: "değer = eldeki miktar × ...". */
export const DEGERLEME_ADI: Record<Degerleme, string> = {
  son: 'son alış fiyatı',
  agort: 'ağırlıklı ortalama maliyet',
};

export const birimMaliyet = (s: Pick<StokDegerSatiri, 'sonAlis' | 'agOrt'>, d: Degerleme) => (d === 'son' ? s.sonAlis : s.agOrt);

/** Değer = miktar × seçili maliyet, kuruşa yuvarlı (satır satır yuvarlanır, toplam bunların toplamıdır). */
export const stokDegeri = (miktar: number, maliyet: number) => yuvarla2(miktar * maliyet);

export type StokDurumu = 'arti' | 'eksi' | 'tumu';

export const DURUM_SECENEKLERI: ReadonlyArray<{ key: StokDurumu; label: string }> = [
  { key: 'arti', label: 'Artılar' },
  { key: 'eksi', label: 'Eksiler' },
  { key: 'tumu', label: 'Tümü' },
];

/** İstemci süzgeci. "Tümü" de eldeki miktarı 0 olan ürünü GÖSTERMEZ. */
export function stokDegerSuz(satirlar: readonly StokDegerSatiri[], durum: StokDurumu, urunGrubu: string, ara: string): StokDegerSatiri[] {
  const durumaUyan = satirlar.filter((s) => (durum === 'arti' ? s.elde > 0 : durum === 'eksi' ? s.elde < 0 : s.elde !== 0));
  return urunSuz(durumaUyan, urunGrubu, ara);
}

/** `lokasyon` ds'i, parametre `@STOK`: bir ürünün lokasyonlara dağılımı (miktarı 0 olan lokasyon gelmez). */
export interface HamLokasyonSatiri {
  lokasyon_id: number | string;
  lokasyon: string | null;
  eldeki_miktar: number | string | null;
}

export interface LokasyonSatiri {
  id: string;
  ad: string;
  elde: number;
}

export function lokasyonSatiri(h: HamLokasyonSatiri): LokasyonSatiri {
  return { id: String(h.lokasyon_id), ad: metinOku(h.lokasyon), elde: sayiOku(h.eldeki_miktar) };
}

// ── Stok Çıkış ──────────────────────────────────────────────────────────────────

/** API boolean'ı metin de döndürebilir. */
const dogruMu = (v: unknown) => v === true || v === 1 || v === 't' || v === 'true' || v === '1';

/** `liste` ds'i, parametreler `@BASTARIH`, `@BITTARIH`, `@LOKASYON`. Satır = ürün × fiş türü. */
export interface HamStokCikisSatiri extends HamStokAlanlari {
  fis_turu_id: number | string | null;
  fis_turu: string | null;
  satis_mi: boolean | string | number | null;
  satir_sayisi: number | string | null;
  miktar: number | string | null;
  tutar: number | string | null;
  kdv_dahil_tutar: number | string | null;
  son_alis_fiyati: number | string | null;
  alis_tutari: number | string | null;
  eldeki_miktar: number | string | null;
}

export type CikisTuru = 'tumu' | 'satis' | 'diger';

export const CIKIS_TURU_SECENEKLERI: ReadonlyArray<{ key: CikisTuru; label: string }> = [
  { key: 'tumu', label: 'Tüm çıkışlar' },
  { key: 'satis', label: 'Yalnız satış' },
  { key: 'diger', label: 'Satış dışı' },
];

/** Bilgi satırındaki ve çekmece alt başlığındaki ad. */
export const CIKIS_TURU_ADI: Record<CikisTuru, string> = {
  tumu: 'tüm çıkışlar',
  satis: 'yalnız satış',
  diger: 'satış dışı çıkışlar',
};

const tureUyar = (tur: CikisTuru, satisMi: boolean) => tur === 'tumu' || (tur === 'satis') === satisMi;

export interface StokCikisSatiri extends StokAlanlari {
  miktar: number;
  /** Tutar / miktar; miktar 0 ise null (boş hücre). */
  ortFiyat: number | null;
  tutar: number;
  kdvDahil: number;
  sonAlis: number;
  alis: number;
  kar: number;
  /** Kâr / alış tutarı (%); alış 0 ise null. */
  karYuzde: number | null;
  elde: number;
}

/** Kâr % = kâr / alış tutarı. Alış (kuruşa yuvarlanınca) 0 ise oran yoktur. */
export function karYuzdesi(kar: number, alis: number): number | null {
  return yuvarla2(alis) === 0 ? null : (kar / alis) * 100;
}

/**
 * Ürün × fiş türü satırlarını "Çıkış türü"ne göre süzer ve ÜRÜN bazında toplar. Miktar, tutar, KDV dahil
 * tutar ve alış tutarı toplanır; son alış fiyatı ve eldeki miktar ürün başına tektir (ilk satırdan).
 * Sıra ds'in sırasıdır (ürün adı). Seçilen türde hiç çıkışı olmayan ürün listede yoktur.
 */
export function cikislariTopla(ham: readonly HamStokCikisSatiri[], tur: CikisTuru): StokCikisSatiri[] {
  const urunler = new Map<string, StokCikisSatiri>();
  for (const h of ham) {
    if (!tureUyar(tur, dogruMu(h.satis_mi))) continue;
    const id = String(h.stok_id);
    let u = urunler.get(id);
    if (!u) {
      u = {
        ...stokAlanlari(h), miktar: 0, ortFiyat: null, tutar: 0, kdvDahil: 0,
        sonAlis: sayiOku(h.son_alis_fiyati), alis: 0, kar: 0, karYuzde: null, elde: sayiOku(h.eldeki_miktar),
      };
      urunler.set(id, u);
    }
    u.miktar += sayiOku(h.miktar);
    u.tutar += sayiOku(h.tutar);
    u.kdvDahil += sayiOku(h.kdv_dahil_tutar);
    u.alis += sayiOku(h.alis_tutari);
  }
  const satirlar = Array.from(urunler.values());
  for (const u of satirlar) {
    u.tutar = yuvarla2(u.tutar);
    u.kdvDahil = yuvarla2(u.kdvDahil);
    u.alis = yuvarla2(u.alis);
    u.ortFiyat = u.miktar === 0 ? null : yuvarla2(u.tutar / u.miktar);
    u.kar = yuvarla2(u.tutar - u.alis);
    u.karYuzde = karYuzdesi(u.kar, u.alis);
  }
  return satirlar;
}

export const stokCikisSuz = (satirlar: readonly StokCikisSatiri[], urunGrubu: string, ara: string) => urunSuz(satirlar, urunGrubu, ara);

// ── Çıkış fişleri (iki ekranın çekmecesi) ───────────────────────────────────────

/**
 * `fisler` ds'i, parametreler `@STOK`, `@BASTARIH`, `@BITTARIH`, `@LOKASYON`: bir ürünün çıkış satırları.
 * `sira` 1 = en yeni.
 */
export interface HamFisSatiri {
  sira: number | string;
  tarih: string | null;
  fis_turu: string | null;
  satis_mi: boolean | string | number | null;
  fis_no: string | null;
  lokasyon: string | null;
  miktar: number | string | null;
  tutar: number | string | null;
}

export interface FisSatiri {
  sira: number;
  tarih: string | null;
  fisTuru: string;
  satisMi: boolean;
  fisNo: string;
  lokasyon: string;
  miktar: number;
  tutar: number;
}

/** Yeniden eskiye sıralı ekran satırları; `tur` verilirse çıkış türüne göre süzülür. */
export function fisSatirlari(ham: readonly HamFisSatiri[], tur: CikisTuru = 'tumu'): FisSatiri[] {
  return ham
    .map((h) => ({
      sira: sayiOku(h.sira),
      tarih: h.tarih || null,
      fisTuru: metinOku(h.fis_turu),
      satisMi: dogruMu(h.satis_mi),
      fisNo: metinOku(h.fis_no),
      lokasyon: metinOku(h.lokasyon),
      miktar: sayiOku(h.miktar),
      tutar: sayiOku(h.tutar),
    }))
    .filter((f) => tureUyar(tur, f.satisMi))
    .sort((a, b) => a.sira - b.sira);
}
